/**
 * The member's derived onboarding progress (`GET /api/onboarding`) as a
 * TanStack query, kept in step with the lawyer slice's `onboardingStatus`
 * (the login gate reads it). The onboarding agent's writes report
 * `invalidate: ["onboarding", ...]`, which refetches it (`settingsEvents`).
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { queryClient } from "@/lib/query/queryClient";

import { setOnboardingStatus } from "../../../redux/lawyer/lawyerSlice";
import { lawyerSelectors } from "../../../redux/lawyer/selectors";
import { store } from "../../../redux/store";
import { OnboardingAPI } from "../api/onboardingApi";
import type { OnboardingProgress } from "../types";

export const ONBOARDING_QUERY_KEY = ["onboarding"] as const;

export const invalidateOnboarding = (): Promise<void> =>
  queryClient.invalidateQueries({ queryKey: ONBOARDING_QUERY_KEY });

/** Put a fresh payload (from start, dismiss or restart) in the cache and the slice. */
export const applyOnboarding = (progress: OnboardingProgress): void => {
  queryClient.setQueryData(ONBOARDING_QUERY_KEY, progress);
  store.dispatch(setOnboardingStatus(progress.status ?? null));
};

export const dismissOnboarding = async (): Promise<OnboardingProgress> => {
  const progress = await OnboardingAPI.dismiss();
  applyOnboarding(progress);
  return progress;
};

export const restartOnboarding = async (): Promise<OnboardingProgress> => {
  const progress = await OnboardingAPI.restart();
  applyOnboarding(progress);
  return progress;
};

export const useOnboarding = (enabled = true) => {
  const dispatch = useDispatch();
  const status = useSelector(lawyerSelectors.selectOnboardingStatus);
  const query = useQuery(
    {
      queryKey: ONBOARDING_QUERY_KEY,
      queryFn: OnboardingAPI.get,
      enabled,
      staleTime: 10_000
    },
    queryClient
  );
  const fresh = query.data?.status ?? null;
  useEffect(() => {
    if (query.data && fresh !== status) dispatch(setOnboardingStatus(fresh));
  }, [query.data, fresh, status, dispatch]);
  return {
    progress: query.data ?? null,
    loading: query.isLoading,
    error: query.error ? String(query.error) : null,
    refetch: query.refetch
  };
};
